import { useId, useState, type FormEvent } from 'react';
import { Image } from 'lucide-react';
import { Dialog, DialogActions } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { CoverPicker } from '../ui/CoverPicker';
import { createItem, titleFromFilename } from '../db/items';
import type { Container } from '../db/board';
import { ImageDecodeError } from '../db/images';
import { ImportField } from '../import/ImportField';
import { useImport } from '../import/useImport';
import '../ui/form.css';
import '../import/ImportField.css';

/**
 * Two ways in: paste a shop link, or pick a photo and name it. A cover photo
 * and a title is still a complete item — the link route just fills in more of
 * one, and both produce exactly the same record.
 */
export function AddItemDialog({
  container,
  onClose,
  onAdded,
  onImported,
}: {
  container: Container;
  onClose: () => void;
  onAdded: (itemId: string) => void;
  /** Opens the panel on the new item so the progress indicator is visible. */
  onImported: (itemId: string) => void;
}) {
  const titleId = useId();

  const [title, setTitle] = useState('');
  const [cover, setCover] = useState<Blob | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const { beginNew } = useImport();

  async function importFrom(url: string) {
    setBusy(true);
    setError(undefined);
    try {
      // Returns as soon as the item exists; the pipeline keeps running and
      // reports into the panel this is about to open.
      onImported(await beginNew(container, url));
    } catch {
      setError("That item couldn't be created. Nothing was added.");
      setBusy(false);
    }
  }

  function pick(file: Blob) {
    setCover(file);
    // The filename is nearly always a better first guess than an empty box,
    // and it's what a dropped file would have given.
    if (title.trim() === '' && file instanceof File) {
      setTitle(titleFromFilename(file.name));
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();

    if (cover === undefined) {
      setError('Pick a photo for this item.');
      return;
    }
    const trimmed = title.trim();
    if (trimmed === '') {
      setError('Give the item a name.');
      return;
    }

    setBusy(true);
    setError(undefined);
    try {
      onAdded(await createItem(container, { title: trimmed, cover }));
    } catch (thrown) {
      setError(
        thrown instanceof ImageDecodeError
          ? thrown.message
          : "That item couldn't be saved. Nothing was added.",
      );
      setBusy(false);
    }
  }

  return (
    <Dialog title="Add Item" onClose={onClose}>
      <form className="dialog__form" onSubmit={(event) => void submit(event)}>
        <ImportField busy={busy} onImport={(url) => void importFrom(url)} />

        <p className="import-or">Or</p>

        <CoverPicker
          label="Photo"
          cover={cover}
          placeholder={<Image size={40} aria-hidden />}
          onPick={pick}
          onClear={() => setCover(undefined)}
        />

        <div className="field">
          <label className="section-label" htmlFor={titleId}>
            Title
          </label>
          <input
            id={titleId}
            className="input"
            value={title}
            placeholder="Linen sofa"
            onChange={(event) => setTitle(event.target.value)}
          />
        </div>

        {error !== undefined && (
          <p className="dialog__error" role="alert">
            {error}
          </p>
        )}

        <DialogActions>
          <Button variant="quiet" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            Add Item
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
